'use client';

import { useSyncExternalStore } from 'react';

interface CapacitorState {
  isNative: boolean;
  isReady: boolean;
}

interface CapacitorGlobal {
  isNativePlatform: () => boolean;
}

function getCapacitorGlobal(): CapacitorGlobal | undefined {
  if (typeof window === 'undefined') return undefined;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const cap = (window as any).Capacitor as CapacitorGlobal | undefined;
  return cap && typeof cap.isNativePlatform === 'function' ? cap : undefined;
}

// Cached snapshots — useSyncExternalStore requires stable references
const NATIVE_STATE: CapacitorState = { isNative: true, isReady: true };
const WEB_STATE: CapacitorState = { isNative: false, isReady: true };
const SERVER_STATE: CapacitorState = { isNative: false, isReady: false };

function getClientSnapshot(): CapacitorState {
  return getCapacitorGlobal()?.isNativePlatform() ? NATIVE_STATE : WEB_STATE;
}

function getServerSnapshot(): CapacitorState {
  return SERVER_STATE;
}

// No-op subscribe — the Capacitor native state never changes at runtime
function subscribe() {
  return () => {};
}

/**
 * Detects if the app is running inside a Capacitor native shell.
 * Returns { isNative, isReady } where isReady prevents hydration flash.
 *
 * Uses useSyncExternalStore for a clean read without useEffect + setState.
 * On the server, isNative=false and isReady=false.
 * On the client, isNative reflects the actual Capacitor state and isReady=true.
 */
export function useCapacitor(): CapacitorState {
  return useSyncExternalStore(subscribe, getClientSnapshot, getServerSnapshot);
}
